import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Switch } from "./components/ui/switch";
import { Label } from "./components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./components/ui/dialog";
import { Ticket, Calendar, CheckCircle2, MapPin, Info, Users } from "lucide-react";

/**
 * SeasonPassBrowse — member-facing card + purchase dialog.
 *
 * Renders a compact banner listing each on-sale season pass with its priced
 * rows. Selecting a row opens a dialog to confirm the purchase before
 * redirecting to Stripe. A member with a linked partner may also add a
 * partner ticket in the same transaction (potentially at a different tier).
 * Members who already have a pass (bought themselves OR bought by their
 * partner) see a "Booked" state.
 *
 * Auto-hides for administrators and when no bookable / booked passes exist.
 */
const SeasonPassBrowse = ({ currentUser }) => {
  const [passes, setPasses] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [pickerPass, setPickerPass] = useState(null);
  const [pickedRowId, setPickedRowId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [detailsPass, setDetailsPass] = useState(null);
  // Partner state — populated from /users/my-partner when the dialog opens.
  const [partner, setPartner] = useState(null);
  const [includePartner, setIncludePartner] = useState(false);
  const [partnerRowId, setPartnerRowId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get("/season-passes");
      setPasses(res.data?.season_passes || []);
    } catch {
      setPasses([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fetch the linked partner once — used to show the "Also buy for my partner"
  // toggle inside the purchase dialog.
  useEffect(() => {
    if (!currentUser || currentUser.role === "Administrator") return;
    axios.get("/users/my-partner")
      .then((res) => setPartner(res.data?.partner || null))
      .catch(() => setPartner(null));
  }, [currentUser]);

  if (!currentUser || currentUser.role === "Administrator") return null;
  // Hide until the first fetch completes to avoid a flash of the empty-state note.
  if (!loaded) return null;

  // Empty state: keep members aware that season passes exist as a feature.
  if (!passes.length) {
    return (
      <div
        className="mb-6 rounded-lg border border-slate-200 bg-slate-50/70 px-4 py-3 flex items-center gap-3"
        data-testid="season-pass-empty-note"
      >
        <div className="rounded-md bg-slate-200/70 p-1.5">
          <Ticket className="h-4 w-4 text-slate-500" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-700">No season passes on sale right now</p>
          <p className="text-xs text-slate-500">
            When a new season opens, you'll see it here — one pass covers every included event.
          </p>
        </div>
      </div>
    );
  }

  const openPicker = (p) => {
    setPickerPass(p);
    // Default-select the cheapest row so the CTA is one click away.
    const rows = (p.price_rows || []).slice().sort((a, b) => (a.amount || 0) - (b.amount || 0));
    setPickedRowId(rows[0]?.id || null);
    setPartnerRowId(rows[0]?.id || null);
    setIncludePartner(false);
  };

  const closePicker = () => {
    setPickerPass(null);
    setIncludePartner(false);
    setPartnerRowId(null);
  };

  const selectedRow = pickerPass?.price_rows?.find((r) => r.id === pickedRowId);
  const selectedPartnerRow = pickerPass?.price_rows?.find((r) => r.id === partnerRowId);
  const memberAmount = Number(selectedRow?.amount ?? 0);
  const partnerAmount = includePartner && partner ? Number(selectedPartnerRow?.amount ?? 0) : 0;
  const totalAmount = memberAmount + partnerAmount;

  const buy = async () => {
    if (!pickerPass || !pickedRowId) return;
    if (includePartner && !partner?.id) {
      toast.error("Link a partner first from your profile before buying a partner ticket.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        price_row_id: pickedRowId,
        origin_url: window.location.origin,
      };
      if (includePartner && partner?.id && partnerRowId) {
        body.partner_price_row_id = partnerRowId;
      }
      const res = await axios.post(`/season-passes/${pickerPass.id}/checkout`, body);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      toast.error("Could not start Stripe checkout.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purchase failed");
    } finally {
      setSubmitting(false);
    }
  };

  const bookedSummary = (p) => {
    // Compose a friendly summary of who's covered by the paid pass.
    if (p.owned_bought_by_partner) {
      return `Your partner booked this pass for you — ${p.owned_price_row_label || "your tier"}.`;
    }
    if (p.owned_bought_for_partner) {
      const partnerTier = p.owned_partner_price_row_label || "partner tier";
      return `Booked for you (${p.owned_price_row_label || "your tier"}) & your partner (${partnerTier}).`;
    }
    return `You've booked this pass — ${p.owned_price_row_label || "your chosen tier"}.`;
  };

  return (
    <div
      className="mb-6 rounded-xl border border-purple-200 bg-gradient-to-r from-purple-50 to-fuchsia-50 p-4 shadow-sm"
      data-testid="season-pass-browse"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-lg bg-purple-600/10 p-2">
          <Ticket className="h-5 w-5 text-purple-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-purple-900">Season Passes on sale</h3>
          <p className="text-xs text-purple-800">
            Buy one pass and enjoy every included event of the season.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {passes.map((p) => {
          if (p.is_owned) {
            return (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => setDetailsPass(p)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetailsPass(p);
                  }
                }}
                className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex flex-col cursor-pointer transition hover:border-emerald-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                data-testid={`season-pass-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{p.season_name}</p>
                    <p className="text-xs text-slate-500">Season {p.display_year || p.season_year_start}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300" data-testid={`season-pass-booked-badge-${p.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Booked
                  </Badge>
                </div>
                <p className="text-xs text-emerald-900 mb-1" data-testid={`season-pass-owned-summary-${p.id}`}>
                  {bookedSummary(p)}
                </p>
                <p className="text-[11px] text-slate-600">
                  Your tickets are generated for each included event — find them under "My Bookings".
                </p>
                <div
                  className="text-[11px] font-medium text-emerald-800 inline-flex items-center gap-1 pt-1"
                  data-testid={`season-pass-details-hint-${p.id}`}
                >
                  <Info className="h-3 w-3" /> Tap for details & included events
                </div>
              </div>
            );
          }
          const rows = p.price_rows || [];
          const eventsCount = (p.events || p.event_ids || []).length;
          return (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => setDetailsPass(p)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setDetailsPass(p);
                }
              }}
              className="rounded-lg border border-purple-200 bg-white p-3 flex flex-col cursor-pointer transition hover:border-purple-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              data-testid={`season-pass-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{p.season_name}</p>
                  <p className="text-xs text-slate-500">
                    Season {p.display_year || p.season_year_start} · {eventsCount} event{eventsCount === 1 ? "" : "s"}
                  </p>
                </div>
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">
                  <Calendar className="h-3 w-3 mr-1" /> On sale
                </Badge>
              </div>

              <div className="text-xs text-slate-600 mb-3 space-y-0.5">
                {rows.length === 0 ? (
                  <div className="italic text-slate-400">No priced rows yet.</div>
                ) : (
                  rows.map((r) => (
                    <div key={r.id}>
                      {r.label} — <strong>€{Number(r.amount ?? 0).toFixed(0)}</strong>
                    </div>
                  ))
                )}
                <div className="text-[10px] italic text-slate-500">
                  Covers events across {(p.required_membership_years || []).join(", ") || "the running year"}
                </div>
                <div
                  className="text-[11px] font-medium text-purple-700 inline-flex items-center gap-1 pt-1"
                  data-testid={`season-pass-details-hint-${p.id}`}
                >
                  <Info className="h-3 w-3" /> Tap for details & included events
                </div>
              </div>

              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  openPicker(p);
                }}
                disabled={rows.length === 0}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                data-testid={`buy-season-pass-${p.id}`}
              >
                Buy
              </Button>
            </div>
          );
        })}
      </div>

      <Dialog open={!!pickerPass} onOpenChange={(v) => !v && closePicker()}>
        <DialogContent className="max-w-md" data-testid="season-pass-buy-dialog">
          <DialogHeader>
            <DialogTitle>{pickerPass?.season_name}</DialogTitle>
            <DialogDescription>
              Choose your ticket type and continue to secure payment. One ticket per included event will be
              generated automatically after payment.
            </DialogDescription>
          </DialogHeader>

          {pickerPass && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Your ticket type</p>
                <div className="grid grid-cols-1 gap-2">
                  {(pickerPass.price_rows || []).map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setPickedRowId(r.id)}
                      className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm text-left ${pickedRowId === r.id ? "border-purple-600 bg-purple-50 text-purple-900 font-semibold" : "border-slate-200"}`}
                      data-testid={`pick-price-row-${r.id}`}
                    >
                      <span>{r.label}</span>
                      <strong>€{Number(r.amount ?? 0).toFixed(2)}</strong>
                    </button>
                  ))}
                </div>
              </div>

              {partner ? (
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Label htmlFor="include-partner-pass" className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 cursor-pointer">
                        <Users className="h-4 w-4 text-purple-600" />
                        Also book & pay for my partner
                      </Label>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {partner.first_name} {partner.last_name} · {partner.email}
                      </p>
                    </div>
                    <Switch
                      id="include-partner-pass"
                      checked={includePartner}
                      onCheckedChange={setIncludePartner}
                      data-testid="include-partner-pass-toggle"
                    />
                  </div>

                  {includePartner && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Partner ticket type</p>
                      <div className="grid grid-cols-1 gap-2" data-testid="partner-price-row-list">
                        {(pickerPass.price_rows || []).map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => setPartnerRowId(r.id)}
                            className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm text-left ${partnerRowId === r.id ? "border-purple-600 bg-purple-50 text-purple-900 font-semibold" : "border-slate-200"}`}
                            data-testid={`partner-pick-price-row-${r.id}`}
                          >
                            <span>{r.label}</span>
                            <strong>€{Number(r.amount ?? 0).toFixed(2)}</strong>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs italic text-slate-500" data-testid="no-partner-note">
                  Link a partner from your profile to buy a season pass for them too.
                </p>
              )}

              <div className="rounded-md bg-slate-50 p-3 text-sm space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Your ticket</span>
                  <span data-testid="season-pass-buy-member-amount">€{memberAmount.toFixed(2)}</span>
                </div>
                {includePartner && partner && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Partner ticket</span>
                    <span data-testid="season-pass-buy-partner-amount">€{partnerAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1 border-t border-slate-200">
                  <span className="text-slate-800 font-semibold">Total to pay</span>
                  <strong className="text-purple-900" data-testid="season-pass-buy-total">
                    €{totalAmount.toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closePicker}>Cancel</Button>
            <Button
              onClick={buy}
              disabled={submitting || !pickedRowId || (includePartner && !partnerRowId)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="season-pass-confirm-buy"
            >
              {submitting ? "Redirecting…" : "Continue to payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pass details dialog — opens when a card is clicked. Shows the price
          rows, sales window, required memberships, and the list of included
          events with date + venue so members can decide before buying. */}
      <Dialog open={!!detailsPass} onOpenChange={(v) => !v && setDetailsPass(null)}>
        <DialogContent className="max-w-lg" data-testid="season-pass-details-dialog">
          <DialogHeader>
            <DialogTitle data-testid="season-pass-details-title">
              {detailsPass?.season_name}
            </DialogTitle>
            <DialogDescription>
              Season {detailsPass?.display_year || detailsPass?.season_year_start} · one pass covers every
              included event below.
            </DialogDescription>
          </DialogHeader>

          {detailsPass && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-700 space-y-1">
                <div>
                  <span className="font-semibold text-slate-800">Sales window:</span>{" "}
                  {detailsPass.sales_window_start || "—"} → {detailsPass.sales_window_end || "—"}
                </div>
                <div>
                  <span className="font-semibold text-slate-800">Covers events across:</span>{" "}
                  {(detailsPass.required_membership_years || []).join(", ") || "the running year"}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
                  Ticket tiers
                </p>
                <div className="space-y-1">
                  {(detailsPass.price_rows || []).length === 0 ? (
                    <p className="text-xs italic text-slate-400">No priced rows yet.</p>
                  ) : (
                    (detailsPass.price_rows || []).map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm"
                        data-testid={`season-pass-details-row-${r.id}`}
                      >
                        <span>{r.label}</span>
                        <strong className="text-purple-900">€{Number(r.amount ?? 0).toFixed(2)}</strong>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
                  Included events ({(detailsPass.events || []).length})
                </p>
                {(detailsPass.events || []).length === 0 ? (
                  <p className="text-xs italic text-slate-400">
                    No events attached to this pass yet.
                  </p>
                ) : (
                  <ul className="space-y-2" data-testid="season-pass-details-events">
                    {(detailsPass.events || []).map((ev) => (
                      <li
                        key={ev.id}
                        className="rounded-md border border-slate-200 bg-white p-2.5 text-sm"
                        data-testid={`season-pass-details-event-${ev.id}`}
                      >
                        <p className="font-medium text-slate-900">{ev.title}</p>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
                          {ev.event_date && (
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(ev.event_date).toLocaleString(undefined, {
                                weekday: "short",
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          )}
                          {ev.venue_name && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {ev.venue_name}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDetailsPass(null)}
              data-testid="season-pass-details-close"
            >
              Close
            </Button>
            {detailsPass && !detailsPass.is_owned && (detailsPass.price_rows || []).length > 0 && (
              <Button
                onClick={() => {
                  const p = detailsPass;
                  setDetailsPass(null);
                  openPicker(p);
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="season-pass-details-buy"
              >
                Buy this pass
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeasonPassBrowse;
